import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Filter } from '@/slices/features/filtersSlice';
import { SearchAgent } from '@/actions/dtos/search/searchAgent';
import { fetchOrganizationAgents } from '@/api/requests/searchAgents';

export enum Period {
  LAST_24H = 'last_24h',
  THIS_WEEK = 'this_week',
  LAST_WEEK = 'last_week',
  WEEK_BEFORE_LAST = 'week_before_last',
}

export const PeriodLabel: Record<Period, string> = {
  [Period.LAST_24H]: 'Letzte 24 Stunden',
  [Period.THIS_WEEK]: 'Diese Woche',
  [Period.LAST_WEEK]: 'Letzte Woche',
  [Period.WEEK_BEFORE_LAST]: 'Vorletzte Woche',
};

export const periodOptions = Object.values(Period).map((code) => ({
  value: code,
  label: PeriodLabel[code],
  name: 'period' as const,
}));

export const loadAgentOptions = createAsyncThunk<Filter[], string>(
  'searchResultsFilters/loadAgentOptions',
  async (organizationId) => {
    const agents: SearchAgent[] = await fetchOrganizationAgents(organizationId);
    return agents.map((a) => ({
      value: a.id,
      label: a.name,
      name: 'searchAgents',
    }));
  },
);

export interface SearchResultsFilterState {
  searchAgents?: Filter[];
  period?: Filter;
  agentOptions: Filter[];
  hasChanges: boolean;
}

const initialState: SearchResultsFilterState = {
  searchAgents: [],
  agentOptions: [],
  period: {
    value: Period.THIS_WEEK,
    label: PeriodLabel[Period.THIS_WEEK],
    name: 'period',
  },
  hasChanges: false,
};

const searchResultsFiltersSlice = createSlice({
  name: 'searchResultsFilters',
  initialState,
  reducers: {
    setAgents(state, action: PayloadAction<Filter[]>) {
      state.searchAgents = action.payload.map((agent) => {
        return { ...agent, name: 'searchAgents' };
      });
    },
    setPeriod(state, action: PayloadAction<Filter>) {
      state.period = { ...action.payload, name: 'period' };
    },
    removeProfilesFilter: (state, action: PayloadAction<string>) => {
      const key = action.payload as keyof SearchResultsFilterState;

      switch (key) {
        case 'searchAgents':
          state.searchAgents = initialState.searchAgents;
          break;
        case 'period':
          state.period = initialState.period;
          break;
      }
    },
    resetSearchResultsFilters() {
      return { ...initialState };
    },
    setHasChanges: (state, action: PayloadAction<boolean>) => {
      state.hasChanges = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadAgentOptions.fulfilled, (state, { payload }) => {
      state.agentOptions = payload;
    });
  },
});

export const { setAgents, removeProfilesFilter, setPeriod, resetSearchResultsFilters, setHasChanges } =
  searchResultsFiltersSlice.actions;

export default searchResultsFiltersSlice.reducer;
